import { useState } from 'react';
import type { BillData } from '@shared/schema/billData';
import type { UserProfile } from '@shared/schema/userProfile';
import { buildAutofillPayload } from '@shared/ird/autofillPayload';

interface CopyAutofillDataButtonProps {
  billData: BillData;
  profile: UserProfile | null;
}

/**
 * Copies the clipboard-handoff payload the autofill bookmarklet reads —
 * see src/bookmarklet/ and docs/automation-architecture.md. Never sends
 * anything to a server; the clipboard IS the handoff channel.
 */
export default function CopyAutofillDataButton({ billData, profile }: CopyAutofillDataButtonProps) {
  const [state, setState] = useState<'idle' | 'copied' | 'error'>('idle');
  const payload = buildAutofillPayload(billData, profile);

  async function handleCopy() {
    if (!payload) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload));
      setState('copied');
      setTimeout(() => setState('idle'), 2000);
    } catch {
      setState('error');
    }
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => void handleCopy()}
        disabled={!payload}
        className="w-full rounded-xl border border-blue-300 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-800 disabled:opacity-50"
      >
        {state === 'copied' ? 'Copied ✓ — now switch tabs and click your bookmarklet' : '⚡ Copy Autofill Data'}
      </button>
      {!payload && <p className="mt-1 text-xs text-slate-500">Missing some required fields — fix them above first.</p>}
      {state === 'error' && (
        <p role="alert" className="mt-1 text-xs text-red-700">
          Couldn't copy to your clipboard. You can still use the fields below manually.
        </p>
      )}
    </div>
  );
}
